import { FC } from 'react';

import MultiComboBox from '@/components/ui/ComboBox/MultiCombobox';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/Form';
import InstructionHint from '@/components/ui/InstructionHint';

import { useLingui } from '@lingui/react';
import { useFormContext } from 'react-hook-form';

export const CAR_PARTS_GROUPS = {
  FRONT_OF_CAR: 'Przód samochodu',
  REAR_OF_CAR: 'Tył samochodu',
  LEFT_SIDE: 'Lewy bok',
  RIGHT_SIDE: 'Prawy bok',
  TOP_OF_CAR: 'Góra samochodu',
} as const;

export const CAR_PARTS = {
  FRONT_OF_CAR: {
    HOOD: 'hood',
    FRONT_BUMPER: 'front_bumper',
    RIGHT_HEADLIGHT: 'right_headlight',
    LEFT_HEADLIGHT: 'left_headlight',
    GRILL: 'grill',
    FRONT_WINDSHIELD: 'front_windshield',
    ENGINE_COVER: 'engine_cover',
    ENGINE: 'engine',
    RADIATOR: 'radiator',
    STEERING_GEAR: 'steering_gear',
  },
  REAR_OF_CAR: {
    REAR_BUMPER: 'rear_bumper',
    TRUNK_COVER: 'trunk_cover',
    RIGHT_REAR_LIGHT: 'right_rear_light',
    LEFT_REAR_LIGHT: 'left_rear_light',
    REAR_WINDSHIELD: 'rear_windshield',
  },
  LEFT_SIDE: {
    LEFT_MIRROR: 'left_mirror',
    LEFT_FRONT_FENDER: 'left_front_fender',
    LEFT_REAR_FENDER: 'left_rear_fender',
    LEFT_FRONT_WHEEL_ARCH: 'left_front_wheel_arch',
    LEFT_REAR_WHEEL_ARCH: 'left_rear_wheel_arch',
    LEFT_FRONT_DOOR: 'left_front_door',
    LEFT_REAR_DOOR: 'left_rear_door',
    LEFT_FRONT_WHEEL: 'left_front_wheel',
    LEFT_REAR_WHEEL: 'left_rear_wheel',
  },
  RIGHT_SIDE: {
    RIGHT_MIRROR: 'right_mirror',
    RIGHT_FRONT_FENDER: 'right_front_fender',
    RIGHT_REAR_FENDER: 'right_rear_fender',
    RIGHT_FRONT_WHEEL_ARCH: 'right_front_wheel_arch',
    RIGHT_REAR_WHEEL_ARCH: 'right_rear_wheel_arch',
    RIGHT_FRONT_DOOR: 'right_front_door',
    RIGHT_REAR_DOOR: 'right_rear_door',
    RIGHT_FRONT_WHEEL: 'right_front_wheel',
    RIGHT_REAR_WHEEL: 'right_rear_wheel',
  },
  TOP_OF_CAR: {
    ROOF: 'roof',
  },
} as const;

export const DAMAGE_BEFORE_ACCIDENT_NAME = 'damages_before_accident';
export const DAMAGE_BEFORE_ACCIDENT_NAME_COMPLEMENTARY =
  'damages_before_accident_complementary';

interface Props {
  name?: string;
  isComplementary?: boolean;
}

export const MultiSelectDamageBeforeAccident: FC<Props> = ({
  name,
  isComplementary = false,
}) => {
  const { i18n } = useLingui();
  const { control } = useFormContext();

  const fieldName =
    name ??
    (isComplementary
      ? DAMAGE_BEFORE_ACCIDENT_NAME_COMPLEMENTARY
      : DAMAGE_BEFORE_ACCIDENT_NAME);

  return (
    <FormField
      control={control}
      name={fieldName}
      rules={{
        required: i18n.t('Uzupełnij to pole, aby przejść dalej'),
      }}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{i18n.t('Co było uszkodzone przed szkodą?')}</FormLabel>
          <InstructionHint
            title={i18n.t('Napisz coś i zatwierdź, klikając ENTER.')}
          />
          <FormControl>
            <MultiComboBox
              field={field}
              placeholder={i18n.t('Wybierz lub wpisz')}
              success={field.value?.length > 0}
              options={[
                {
                  label: i18n.t('Maska'),
                  value: CAR_PARTS.FRONT_OF_CAR.HOOD,
                  group: CAR_PARTS_GROUPS.FRONT_OF_CAR,
                },
                {
                  label: i18n.t('Zderzak przedni'),
                  value: CAR_PARTS.FRONT_OF_CAR.FRONT_BUMPER,
                  group: CAR_PARTS_GROUPS.FRONT_OF_CAR,
                },
                {
                  label: i18n.t('Reflektor prawy'),
                  value: CAR_PARTS.FRONT_OF_CAR.RIGHT_HEADLIGHT,
                  group: CAR_PARTS_GROUPS.FRONT_OF_CAR,
                },
                {
                  label: i18n.t('Reflektor lewy'),
                  value: CAR_PARTS.FRONT_OF_CAR.LEFT_HEADLIGHT,
                  group: CAR_PARTS_GROUPS.FRONT_OF_CAR,
                },
                {
                  label: i18n.t('Grill'),
                  value: CAR_PARTS.FRONT_OF_CAR.GRILL,
                  group: CAR_PARTS_GROUPS.FRONT_OF_CAR,
                },
                {
                  label: i18n.t('Szyba przednia'),
                  value: CAR_PARTS.FRONT_OF_CAR.FRONT_WINDSHIELD,
                  group: CAR_PARTS_GROUPS.FRONT_OF_CAR,
                },
                {
                  label: i18n.t('Pokrywa silnika'),
                  value: CAR_PARTS.FRONT_OF_CAR.ENGINE_COVER,
                  group: CAR_PARTS_GROUPS.FRONT_OF_CAR,
                },
                {
                  label: i18n.t('Silnik'),
                  value: CAR_PARTS.FRONT_OF_CAR.ENGINE,
                  group: CAR_PARTS_GROUPS.FRONT_OF_CAR,
                },
                {
                  label: i18n.t('Chłodnica'),
                  value: CAR_PARTS.FRONT_OF_CAR.RADIATOR,
                  group: CAR_PARTS_GROUPS.FRONT_OF_CAR,
                },
                {
                  label: i18n.t('Maglownica'),
                  value: CAR_PARTS.FRONT_OF_CAR.STEERING_GEAR,
                  group: CAR_PARTS_GROUPS.FRONT_OF_CAR,
                },
                {
                  label: i18n.t('Zderzak tylny'),
                  value: CAR_PARTS.REAR_OF_CAR.REAR_BUMPER,
                  group: CAR_PARTS_GROUPS.REAR_OF_CAR,
                },
                {
                  label: i18n.t('Pokrywa bagażnika'),
                  value: CAR_PARTS.REAR_OF_CAR.TRUNK_COVER,
                  group: CAR_PARTS_GROUPS.REAR_OF_CAR,
                },
                {
                  label: i18n.t('Lampa prawa'),
                  value: CAR_PARTS.REAR_OF_CAR.RIGHT_REAR_LIGHT,
                  group: CAR_PARTS_GROUPS.REAR_OF_CAR,
                },
                {
                  label: i18n.t('Lampa lewa'),
                  value: CAR_PARTS.REAR_OF_CAR.LEFT_REAR_LIGHT,
                  group: CAR_PARTS_GROUPS.REAR_OF_CAR,
                },
                {
                  label: i18n.t('Szyba tylna'),
                  value: CAR_PARTS.REAR_OF_CAR.REAR_WINDSHIELD,
                  group: CAR_PARTS_GROUPS.REAR_OF_CAR,
                },
                {
                  label: i18n.t('Lusterko lewe'),
                  value: CAR_PARTS.LEFT_SIDE.LEFT_MIRROR,
                  group: CAR_PARTS_GROUPS.LEFT_SIDE,
                },
                {
                  label: i18n.t('Błotnik lewy przedni'),
                  value: CAR_PARTS.LEFT_SIDE.LEFT_FRONT_FENDER,
                  group: CAR_PARTS_GROUPS.LEFT_SIDE,
                },
                {
                  label: i18n.t('Błotnik lewy tylny'),
                  value: CAR_PARTS.LEFT_SIDE.LEFT_REAR_FENDER,
                  group: CAR_PARTS_GROUPS.LEFT_SIDE,
                },
                {
                  label: i18n.t('Nadkole lewe przednie'),
                  value: CAR_PARTS.LEFT_SIDE.LEFT_FRONT_WHEEL_ARCH,
                  group: CAR_PARTS_GROUPS.LEFT_SIDE,
                },
                {
                  label: i18n.t('Nadkole lewe tylne'),
                  value: CAR_PARTS.LEFT_SIDE.LEFT_REAR_WHEEL_ARCH,
                  group: CAR_PARTS_GROUPS.LEFT_SIDE,
                },
                {
                  label: i18n.t('Drzwi lewe przednie'),
                  value: CAR_PARTS.LEFT_SIDE.LEFT_FRONT_DOOR,
                  group: CAR_PARTS_GROUPS.LEFT_SIDE,
                },
                {
                  label: i18n.t('Drzwi lewe tylne'),
                  value: CAR_PARTS.LEFT_SIDE.LEFT_REAR_DOOR,
                  group: CAR_PARTS_GROUPS.LEFT_SIDE,
                },
                {
                  label: i18n.t('Felga przednia lewa'),
                  value: CAR_PARTS.LEFT_SIDE.LEFT_FRONT_WHEEL,
                  group: CAR_PARTS_GROUPS.LEFT_SIDE,
                },
                {
                  label: i18n.t('Felga tylna lewa'),
                  value: CAR_PARTS.LEFT_SIDE.LEFT_REAR_WHEEL,
                  group: CAR_PARTS_GROUPS.LEFT_SIDE,
                },
                {
                  label: i18n.t('Lusterko prawe'),
                  value: CAR_PARTS.RIGHT_SIDE.RIGHT_MIRROR,
                  group: CAR_PARTS_GROUPS.RIGHT_SIDE,
                },
                {
                  label: i18n.t('Błotnik prawy przedni'),
                  value: CAR_PARTS.RIGHT_SIDE.RIGHT_FRONT_FENDER,
                  group: CAR_PARTS_GROUPS.RIGHT_SIDE,
                },
                {
                  label: i18n.t('Błotnik prawy tylny'),
                  value: CAR_PARTS.RIGHT_SIDE.RIGHT_REAR_FENDER,
                  group: CAR_PARTS_GROUPS.RIGHT_SIDE,
                },
                {
                  label: i18n.t('Nadkole prawe przednie'),
                  value: CAR_PARTS.RIGHT_SIDE.RIGHT_FRONT_WHEEL_ARCH,
                  group: CAR_PARTS_GROUPS.RIGHT_SIDE,
                },
                {
                  label: i18n.t('Nadkole prawe tylne'),
                  value: CAR_PARTS.RIGHT_SIDE.RIGHT_REAR_WHEEL_ARCH,
                  group: CAR_PARTS_GROUPS.RIGHT_SIDE,
                },
                {
                  label: i18n.t('Drzwi prawe przednie'),
                  value: CAR_PARTS.RIGHT_SIDE.RIGHT_FRONT_DOOR,
                  group: CAR_PARTS_GROUPS.RIGHT_SIDE,
                },
                {
                  label: i18n.t('Drzwi prawe tylne'),
                  value: CAR_PARTS.RIGHT_SIDE.RIGHT_REAR_DOOR,
                  group: CAR_PARTS_GROUPS.RIGHT_SIDE,
                },
                {
                  label: i18n.t('Felga przednia prawa'),
                  value: CAR_PARTS.RIGHT_SIDE.RIGHT_FRONT_WHEEL,
                  group: CAR_PARTS_GROUPS.RIGHT_SIDE,
                },
                {
                  label: i18n.t('Felga tylna prawa'),
                  value: CAR_PARTS.RIGHT_SIDE.RIGHT_REAR_WHEEL,
                  group: CAR_PARTS_GROUPS.RIGHT_SIDE,
                },
                {
                  label: i18n.t('Dach'),
                  value: CAR_PARTS.TOP_OF_CAR.ROOF,
                  group: CAR_PARTS_GROUPS.TOP_OF_CAR,
                },
              ]}
              data-testid="multi-select-damage-before-accident"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
